import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  Collapse,
  IconButton,
  Divider,
  Tooltip,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useParams, useNavigate } from 'react-router-dom';
import SyncRounded from '@mui/icons-material/SyncRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import PendingRounded from '@mui/icons-material/PendingRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import MergeTypeRounded from '@mui/icons-material/MergeTypeRounded';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { buildApiUrl } from '../config';
import { SnackbarContext } from '../ui/SnackbarProvider';
import { markProductStep } from '../hooks/useProductOnboarding';
import { SyncJobContext } from '../ui/SyncJobProvider';
import { formatRelativeTime } from '../utils/date';
import {
  SyncJob,
  Step,
  SubStep,
  SubSubStep,
  StepStatus,
  StatusChip,
  parseUtcDate,
  formatDuration,
} from './DataSyncPage';

// ---------------------------------------------------------------------------
// Error message parser
// Splits "Headline sentence\n\nAffected files:\n  • file1\n  • file2"
// into a structured object for rendering.
// ---------------------------------------------------------------------------

interface ParsedError {
  headline: string;
  detail: string | null;
  affectedFiles: string[];
}

function parseErrorMessage(raw: string): ParsedError {
  const [mainPart, filesPart] = raw.split(/\n\nAffected files:\n/);
  const affectedFiles = filesPart
    ? filesPart.split('\n').map(l => l.replace(/^\s*•\s*/, '').trim()).filter(Boolean)
    : [];
  // Split headline (up to first ': ') from detail body
  const colonIdx = mainPart.indexOf(': ');
  const headline = colonIdx !== -1 ? mainPart.slice(0, colonIdx) : mainPart;
  const detail   = colonIdx !== -1 ? mainPart.slice(colonIdx + 2) : null;
  return { headline, detail, affectedFiles };
}

/** One-liner suitable for a toast — first sentence, max 120 chars. */
function briefError(raw: string): string {
  const first = raw.split('\n')[0];
  return first.length > 120 ? first.slice(0, 117) + '…' : first;
}

// ---------------------------------------------------------------------------
// Status icon
// ---------------------------------------------------------------------------

function StatusIcon({ status, size = 18 }: { status: StepStatus; size?: number }) {
  const theme = useTheme();
  if (status === 'completed') return <CheckCircleRounded sx={{ fontSize: size, color: theme.palette.success.main }} />;
  if (status === 'failed')    return <ErrorRounded sx={{ fontSize: size, color: theme.palette.error.main }} />;
  if (status === 'running')   return <CircularProgress size={size - 2} thickness={5} />;
  return <PendingRounded sx={{ fontSize: size, color: theme.palette.text.disabled }} />;
}

// ---------------------------------------------------------------------------
// Step icon + label normalisation (backend still sends old names)
// ---------------------------------------------------------------------------

function stepIcon(stepId: string, size = 18) {
  if (stepId === 'refresh_schema') return <RefreshRounded sx={{ fontSize: size }} />;
  if (stepId === 'profiling')      return <TableChartRounded sx={{ fontSize: size }} />;
  if (stepId === 'build_ontology') return <AccountTreeRounded sx={{ fontSize: size }} />;
  return <StorageRounded sx={{ fontSize: size }} />;
}

function normaliseStepLabel(label: string): string {
  if (label === 'Build Data Relationships') return 'Build Data Model';
  return label;
}

const STEP_DESCRIPTIONS: Record<string, string> = {
  refresh_schema:  'Fetches the latest table and column structure from each connector',
  profiling:       'Computes statistics and data quality metrics for selected tables',
  build_ontology:  'AI discovers entities, maps fields, and generates relationships',
};

function stepDescription(stepId: string): string | null {
  return STEP_DESCRIPTIONS[stepId] ?? null;
}

// ---------------------------------------------------------------------------
// Sub-substep row (e.g. individual table inside profiling)
// ---------------------------------------------------------------------------

const SubSubStepRow: React.FC<{ subStep: SubSubStep }> = ({ subStep }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: { xs: 2, sm: 4 }, py: 0.25, minWidth: 0 }}>
      <StatusIcon status={subStep.status} size={14} />
      <Typography variant="caption" noWrap title={subStep.label} sx={{ color: theme.palette.text.secondary, flex: '1 1 0%', minWidth: 0 }}>
        {subStep.label}
      </Typography>
      {subStep.error && (
        <Tooltip title={subStep.error} placement="top">
          <Typography variant="caption" color="error"
            sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subStep.error}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Sub-step row (connector within refresh/profiling, or phase within ontology)
// ---------------------------------------------------------------------------

const SubStepRow: React.FC<{ ss: SubStep; defaultExpanded?: boolean }> = ({ ss, defaultExpanded = false }) => {
  const theme = useTheme();
  const hasChildren = (ss.substeps || []).length > 0;
  const [open, setOpen] = React.useState(defaultExpanded);

  React.useEffect(() => {
    if (ss.status === 'running') setOpen(true);
  }, [ss.status]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1,
          pl: { xs: 1, sm: 2 }, py: 0.4, borderRadius: 1,
          minWidth: 0,
          cursor: hasChildren ? 'pointer' : 'default',
          '&:hover': hasChildren ? { bgcolor: theme.palette.action.hover } : {},
        }}
        onClick={() => hasChildren && setOpen((v: boolean) => !v)}
      >
        <Box sx={{ mt: 0.2, flexShrink: 0 }}>
          <StatusIcon status={ss.status} size={16} />
        </Box>
        <Box sx={{ flex: '1 1 0%', minWidth: 0 }}>
          <Typography variant="body2" noWrap title={ss.label} sx={{ fontWeight: ss.status === 'running' ? 600 : 400 }}>
            {ss.label}
          </Typography>
          {/* Detail text — split on ' · ' so each item gets its own line */}
          {(ss as any).detail && (ss.status === 'completed' || ss.status === 'running') && (
            <Box sx={{ mt: 0.15 }}>
              {String((ss as any).detail).split(' · ').map((line: string, i: number) => (
                <Typography
                  key={i}
                  variant="caption"
                  sx={(t: any) => ({
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '5px',
                    color: t.tokens?.text?.secondary ?? t.palette.text.primary,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    lineHeight: 1.6,
                    '&::before': {
                      content: '"–"',
                      flexShrink: 0,
                      opacity: 0.45,
                      fontSize: '0.7rem',
                    },
                  })}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
        {ss.error && ss.status === 'failed' && (
          <Tooltip title={ss.error} placement="top">
            <Typography variant="caption" color="error"
              sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {ss.error}
            </Typography>
          </Tooltip>
        )}
        {hasChildren && (
          <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }}>
            {open ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
          </IconButton>
        )}
      </Box>
      {hasChildren && (
        <Collapse in={open} unmountOnExit>
          {(ss.substeps || []).map((subStep) => (
            <SubSubStepRow key={subStep.id} subStep={subStep} />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Top-level step accordion
// ---------------------------------------------------------------------------

const StepAccordion: React.FC<{ step: Step }> = ({ step }) => {
  const theme = useTheme();
  const hasChildren = (step.substeps || []).length > 0;
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (step.status === 'running') setOpen(true);
  }, [step.status]);

  const borderColor =
    step.status === 'completed' ? theme.palette.success.main :
    step.status === 'failed'    ? theme.palette.error.main   :
    step.status === 'running'   ? theme.palette.primary.main :
    theme.palette.divider;

  return (
    <Paper variant="outlined" sx={{ borderColor, borderRadius: 2, overflow: 'hidden', transition: 'border-color 0.3s' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          rowGap: 1,
          px: { xs: 1.5, sm: 2 }, py: 1.25,
          cursor: hasChildren ? 'pointer' : 'default',
          bgcolor: step.status === 'running'
            ? (theme.palette.mode === 'dark' ? 'rgba(25,118,210,0.08)' : 'rgba(25,118,210,0.04)')
            : 'transparent',
          '&:hover': hasChildren ? { bgcolor: theme.palette.action.hover } : {},
        }}
        onClick={() => hasChildren && setOpen((v: boolean) => !v)}
      >
        <Box sx={{ color: theme.palette.text.secondary, display: 'flex', flexShrink: 0 }}>
          {stepIcon(step.id)}
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{normaliseStepLabel(step.label)}</Typography>
          {stepDescription(step.id) && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3, mt: 0.15 }}>
              {stepDescription(step.id)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: { xs: 'auto', sm: 0 } }}>
          <StatusIcon status={step.status} size={20} />
          {step.error && step.status === 'failed' && (
            <Tooltip title={step.error} placement="top">
              <Typography variant="caption" color="error"
                sx={{ maxWidth: { xs: 120, sm: 240 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {step.error}
              </Typography>
            </Tooltip>
          )}
          {hasChildren && (
            <IconButton size="small" sx={{ p: 0.25 }}>
              {open ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>
      {hasChildren && (
        <Collapse in={open} unmountOnExit>
          <Divider />
          <Box sx={{ py: 0.75, px: { xs: 0.5, sm: 1 } }}>
            {(step.substeps || []).map(ss => (
              <SubStepRow key={ss.id} ss={ss} defaultExpanded={step.id === 'profiling'} />
            ))}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Delta summary types (mirror of backend services/delta_processor.py)
// ---------------------------------------------------------------------------

interface TableAddedOutcome {
  connector_id: string;
  source_table: string;
  total_columns: number;
  candidates: Array<{ entity: string; score: number; via: string }>;
  decision: 'merged' | 'new_entity' | 'unassigned';
  target_entity: string | null;
  target_entity_id: string | null;
  fields_merged: number;
  fields_added: number;
  fields_ignored: number;
  sample_merged_fields: Array<{ source: string; canonical: string }>;
  sample_added_fields: string[];
}

interface DeltaSummary {
  tables_added: TableAddedOutcome[];
  tables_removed: Array<{ connector_id: string; source_table: string }>;
  new_entities: string[];
  new_relationships: number;
}

// ---------------------------------------------------------------------------
// Delta summary panel — "What changed in this run"
// ---------------------------------------------------------------------------

const DeltaSummaryPanel: React.FC<{ summary: DeltaSummary; onNavigateModel: () => void }> = ({ summary, onNavigateModel }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set([0]));

  const totalTablesAdded = summary.tables_added.length;
  const totalTablesRemoved = summary.tables_removed.length;
  const totalFieldsMerged = summary.tables_added.reduce((s: number, t: TableAddedOutcome) => s + t.fields_merged, 0);
  const totalFieldsAdded = summary.tables_added.reduce((s: number, t: TableAddedOutcome) => s + t.fields_added, 0);
  const newEntities = summary.new_entities.length;
  const newRels = summary.new_relationships;

  const hasChanges = totalTablesAdded > 0 || totalTablesRemoved > 0 || newEntities > 0;
  if (!hasChanges) return null;

  const toggleExpanded = (i: number) => {
    setExpanded((prev: Set<number>) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const decisionChip = (decision: TableAddedOutcome['decision']) => {
    if (decision === 'merged') return (
      <Chip icon={<MergeTypeRounded sx={{ fontSize: '0.875rem !important' }} />}
        label="Merged into existing entity" size="small" color="primary" variant="outlined"
        sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600 }} />
    );
    if (decision === 'new_entity') return (
      <Chip icon={<AddCircleOutlineRounded sx={{ fontSize: '0.875rem !important' }} />}
        label="New entity created" size="small" color="success" variant="outlined"
        sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600 }} />
    );
    return (
      <Chip icon={<WarningAmberRounded sx={{ fontSize: '0.875rem !important' }} />}
        label="Needs assignment" size="small" color="warning" variant="outlined"
        sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600 }} />
    );
  };

  return (
      <Paper variant="outlined" sx={(t: any) => ({ borderRadius: 2, p: 2, mb: 2, borderColor: t.tokens?.border?.subtle })}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountTreeRounded sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>What changed in this run</Typography>
        </Stack>
        <Button size="small" variant="text" onClick={onNavigateModel} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          Open Data Model →
        </Button>
      </Stack>

      {/* Headline stats */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1.75 }}>
        {totalTablesAdded > 0 && (
          <Chip size="small" variant="filled" color="primary"
            label={`${totalTablesAdded} new table${totalTablesAdded !== 1 ? 's' : ''} detected`}
            sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600 }} />
        )}
        {totalFieldsMerged > 0 && (
          <Chip size="small" variant="outlined"
            label={`${totalFieldsMerged} field${totalFieldsMerged !== 1 ? 's' : ''} merged`}
            sx={{ fontSize: '0.6875rem', height: 22 }} />
        )}
        {totalFieldsAdded > 0 && (
          <Chip size="small" variant="outlined"
            label={`${totalFieldsAdded} new field${totalFieldsAdded !== 1 ? 's' : ''}`}
            sx={{ fontSize: '0.6875rem', height: 22 }} />
        )}
        {newEntities > 0 && (
          <Chip size="small" variant="outlined" color="success"
            label={`${newEntities} new entit${newEntities !== 1 ? 'ies' : 'y'} created`}
            sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600 }} />
        )}
        {newRels > 0 && (
          <Chip size="small" variant="outlined"
            label={`${newRels} new relationship${newRels !== 1 ? 's' : ''}`}
            sx={{ fontSize: '0.6875rem', height: 22 }} />
        )}
        {totalTablesRemoved > 0 && (
          <Chip size="small" variant="outlined" color="error"
            label={`${totalTablesRemoved} table${totalTablesRemoved !== 1 ? 's' : ''} removed`}
            sx={{ fontSize: '0.6875rem', height: 22 }} />
        )}
      </Stack>

      {/* Per-table details */}
      <Stack spacing={1}>
        {summary.tables_added.map((t: TableAddedOutcome, i: number) => {
          const isOpen = expanded.has(i);
          return (
            <Box key={i} sx={(th: any) => ({
              border: `1px solid ${th.tokens?.border?.subtle ?? th.palette.divider}`,
              borderRadius: 1.5, overflow: 'hidden',
            })}>
              {/* Table row header */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => toggleExpanded(i)}
              >
                <TableChartRounded sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {t.source_table}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                    {t.total_columns} column{t.total_columns !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                {decisionChip(t.decision)}
                {t.target_entity && (
                  <Chip size="small" label={t.target_entity}
                    sx={(th: any) => ({ fontSize: '0.6875rem', height: 22, fontWeight: 600, bgcolor: th.palette.primary.main + '18', color: 'primary.main', flexShrink: 0 })} />
                )}
                <IconButton size="small" tabIndex={-1} sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                  <ExpandMoreRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              {/* Expanded detail — counts only, no field name lists */}
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Divider />
                <Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {t.fields_merged > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      <Box component="span" fontWeight={600}>{t.fields_merged}</Box> field{t.fields_merged !== 1 ? 's' : ''} mapped to existing {t.target_entity} fields
                    </Typography>
                  )}
                  {t.fields_added > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      <Box component="span" fontWeight={600}>{t.fields_added}</Box> new field{t.fields_added !== 1 ? 's' : ''} added to {t.target_entity}
                    </Typography>
                  )}
                  {t.fields_ignored > 0 && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                      {t.fields_ignored} ignored
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          );
        })}

        {/* Removed tables */}
        {summary.tables_removed.map((t: { connector_id: string; source_table: string }, i: number) => (
          <Box key={`removed-${i}`} sx={(th: any) => ({
            border: `1px solid ${th.palette.error.light}`,
            borderRadius: 1.5, px: 1.5, py: 1,
            display: 'flex', alignItems: 'center', gap: 1,
          })}>
            <TableChartRounded sx={{ fontSize: 15, color: 'error.main', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8125rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }} noWrap>
              {t.source_table}
            </Typography>
            <Chip size="small" label="Removed" color="error" variant="outlined"
              sx={{ fontSize: '0.6875rem', height: 22, fontWeight: 600 }} />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Job detail page
// ---------------------------------------------------------------------------

export default function DataSyncJobPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { notify } = React.useContext(SnackbarContext);
  const { notifyJobUpdate } = React.useContext(SyncJobContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tenantId, setTenantId] = React.useState<string | null>(null);
  const [job, setJob] = React.useState<SyncJob | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  // Track whether the job was actively running when we started polling,
  // so we only toast on live transitions - not when loading a historical job.
  const wasLiveRef = React.useRef(false);
  const isFirstFetchRef = React.useRef(true);

  const authHeader = React.useCallback(() => {
    const token = localStorage.getItem('klaris_jwt');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Load tenant id
  React.useEffect(() => {
    const raw = localStorage.getItem('klaris_tenant');
    if (!raw) return;
    try {
      const t = JSON.parse(raw);
      setTenantId(t.tenant_id || t.id || null);
    } catch { setTenantId(raw); }
  }, []);

  // Fetch job
  const fetchJob = React.useCallback(async () => {
    if (!tenantId || !jobId) return;
    try {
      const r = await fetch(buildApiUrl(`/api/v1/tenants/${tenantId}/sync-jobs/${jobId}`), {
        headers: authHeader() as HeadersInit,
      });
      if (!r.ok) { setNotFound(true); return; }
      const data: SyncJob = await r.json();
      setJob(data);

      const isFirst = isFirstFetchRef.current;
      isFirstFetchRef.current = false;

      // Mark as live only if the job was running/pending on first load
      if (isFirst && (data.status === 'running' || data.status === 'pending')) {
        wasLiveRef.current = true;
        // Immediately surface the job in the global banner without waiting for its next poll
        notifyJobUpdate(data as any);
      }

      if (data.status === 'completed' || data.status === 'failed') {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        // Clear the global banner immediately - don't wait for the provider's next poll
        notifyJobUpdate(null);
        // Only show toast for transitions we observed live, not historical loads
        if (wasLiveRef.current) {
          if (data.status === 'completed') {
            // Build an informative toast if we have delta data
            const ds: DeltaSummary | undefined = (data.progress as any)?.delta_summary;
            const tablesAdded: number = ds?.tables_added?.length ?? 0;
            const newEntities: number = ds?.new_entities?.length ?? 0;
            const totalMerged: number = ds?.tables_added?.reduce((s: number, ta: TableAddedOutcome) => s + ta.fields_merged, 0) ?? 0;
            let toastMsg = 'Data sync completed successfully!';
            if (tablesAdded > 0 && ds?.tables_added?.[0]?.target_entity) {
              const first: TableAddedOutcome = ds.tables_added[0];
              if (tablesAdded === 1) {
                toastMsg = first.decision === 'merged'
                  ? `Sync complete. "${first.source_table}" merged into ${first.target_entity} (${first.fields_merged + first.fields_added} fields).`
                  : `Sync complete. New entity "${first.target_entity}" created from ${first.source_table}.`;
              } else {
                toastMsg = `Sync complete. ${tablesAdded} new tables detected, ${newEntities > 0 ? `${newEntities} new entit${newEntities !== 1 ? 'ies' : 'y'} created` : `${totalMerged} fields merged`}.`;
              }
            }
            notify(toastMsg, 'success');
            markProductStep('data_model_built');
            // Self-serve onboarding: user stayed on this page while sync ran - go to Copilot for the next step.
            let selfServe = false;
            try {
              const raw = localStorage.getItem('klaris_tenant');
              if (raw) selfServe = JSON.parse(raw).onboarding_source === 'self_serve';
            } catch { /* ignore */ }
            if (selfServe) {
              navigate('/copilot', { replace: true });
            }
          }
          else notify(briefError(data.error_message || 'Sync failed'), 'error');
          wasLiveRef.current = false;
        }
      }
    } catch (e) {
      console.error('Failed to fetch job', e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, jobId, authHeader, notify, notifyJobUpdate, navigate]);

  React.useEffect(() => {
    if (!tenantId || !jobId) return;
    fetchJob();
  }, [fetchJob]);

  // Start polling when job is running/pending
  React.useEffect(() => {
    if (!job) return;
    if (job.status === 'running' || job.status === 'pending') {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchJob, 5000);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [job?.status, fetchJob]);

  // Cleanup on unmount
  React.useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Live elapsed timer
  React.useEffect(() => {
    if (!job || (job.status !== 'running' && job.status !== 'pending')) return;
    const start = parseUtcDate(job.created_at).getTime();
    const tick = () => setElapsed(Date.now() - start);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [job?.status, job?.created_at]);

  const finalDuration = React.useMemo(() => {
    if (!job?.completed_at) return null;
    const ms = parseUtcDate(job.completed_at).getTime() - parseUtcDate(job.created_at).getTime();
    return formatDuration(ms);
  }, [job?.completed_at, job?.created_at]);

  // ---------------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !job) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>Sync job not found.</Alert>
        <Button startIcon={<ArrowBackRounded />} onClick={() => navigate('/data-sync')}>
          Back to Data Sync
        </Button>
      </Box>
    );
  }

  const steps: Step[] = job.progress?.steps || [];

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: { xs: 2, md: 0 } }}>
      {/* Page header */}
      <Box sx={(t) => ({ mb: 2, pb: 1.5, borderBottom: `1px solid ${t.tokens.border.subtle}` })}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 2, md: 0 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{ flexWrap: 'wrap', gap: 1, rowGap: 0.75 }}
          >
            <Button
              size="small"
              variant="text"
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate('/data-sync')}
              sx={{ color: 'text.secondary' }}
            >
              Data Sync
            </Button>
            <StatusChip status={job.status} />
            {(job.status === 'running' || job.status === 'pending') && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                {formatDuration(elapsed)} elapsed
              </Typography>
            )}
            {finalDuration && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                took {finalDuration}
              </Typography>
            )}
            {job.completed_at && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                · {formatRelativeTime(job.completed_at)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
              · {job.connector_ids.length} connector{job.connector_ids.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>

          {(job.status === 'completed' || job.status === 'failed') && (
            <Button
              variant="outlined"
              startIcon={<SyncRounded />}
              size="small"
              fullWidth={isMobile}
              onClick={() => navigate('/data-sync')}
              sx={{ flexShrink: 0 }}
            >
              Start New Sync
            </Button>
          )}
        </Stack>
      </Box>

      {/* Alert banners */}
      {job.status === 'failed' && job.error_message && (() => {
        const { headline, detail, affectedFiles } = parseErrorMessage(job.error_message);
        return (
          <Alert severity="error" sx={{ mb: 2, alignItems: 'flex-start' }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: detail || affectedFiles.length ? 0.5 : 0 }}>
              {headline}
            </Typography>
            {detail && (
              <Typography variant="body2" sx={{ mb: affectedFiles.length ? 0.75 : 0 }}>
                {detail}
              </Typography>
            )}
            {affectedFiles.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {affectedFiles.map((f, i) => (
                  <Typography key={i} component="li" variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8125rem' }}>
                    {f}
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        );
      })()}
      {job.status === 'completed' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Sync completed successfully. Your schema, profiles, and data model are up to date.
        </Alert>
      )}

      {/* Delta summary — "What changed" panel (only shown for incremental syncs with data) */}
      {job.status === 'completed' && (job.progress as any)?.delta_summary && (
        <DeltaSummaryPanel
          summary={(job.progress as any).delta_summary as DeltaSummary}
          onNavigateModel={() => navigate('/data-model')}
        />
      )}

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2.5, md: 3 }, alignItems: 'stretch' }}>
        <Stack spacing={1.5} sx={{ flex: '1 1 0', minWidth: 0, order: { xs: 1, md: 1 } }}>
          {steps.map(step => <StepAccordion key={step.id} step={step} />)}
        </Stack>

        {/* Right: summary card */}
        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, order: { xs: 2, md: 2 } }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 1.75, sm: 2 } }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem', display: 'block', mb: 1.5 }}>
              Sync Summary
            </Typography>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.25 }}><StatusChip status={job.status} /></Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Connectors</Typography>
                <Typography variant="body2" fontWeight={500}>{job.connector_ids.length}</Typography>
              </Box>
              {finalDuration && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Duration</Typography>
                  <Typography variant="body2" fontWeight={500}>{finalDuration}</Typography>
                </Box>
              )}
              {job.completed_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Completed</Typography>
                  <Typography variant="body2" fontWeight={500}>{formatRelativeTime(job.completed_at)}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Started</Typography>
                <Typography variant="body2" fontWeight={500}>{formatRelativeTime(job.created_at)}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
